import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { Save, Loader2, Download, RotateCcw, Plus, Trash2, ChevronDown, ChevronRight, Lock, MessageSquare } from 'lucide-react';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-SALARY-SIMPLE — one driver, one period, one row per line.
//
// Every amount is held as the raw text the operator typed and only turned into
// a number when it is used. Storing numbers instead made the boxes fight back:
// a cleared box became 0, "1." could not be typed on the way to "1.5", and a
// leading zero had to be deleted before anything else could go in.
//
// Each row is: what it is, one box, a direction, the effect. The figure the
// system worked out sits under the label rather than in a column of its own,
// so there is one place to look and one place to type.

export type Sign = 'plus' | 'minus';

export type LineKey =
  | 'baseSalary' | 'perKm'
  | 'dieselPetroapp' | 'dieselAldrees'
  | 'cashReceived' | 'cashReceivedHala' | 'maintenance';

/** A row the operator wrote by hand. Amount stays text while being edited. */
interface CustomRow { label: string; amount: string; sign: Sign }

interface Line { derived: number; override: number | null; value: number }

interface TripRow {
  id: string; code: string; date: string;
  vehiclePlate: string | null; vehicleCode: string | null;
  routeFrom: string | null; routeTo: string | null; clientName?: string | null;
  km: number | null; tons: number | null;
  revenueSar: number | null; tripAmountSar: number | null;
  status: string;
}

export interface Sheet {
  /** NEOM only: the per-trip line. Absent on the dumpers sheet. */
  perTrip?: PerTripLine | null;
  /** The same money, split by the site the trips went to. */
  perTripSites?: PerTripSite[];
  driver: { id: string; code: string; name: string };
  period: { from: string; to: string; openEnded: boolean };
  rates: { perTripRateSar: number | null; perKmRateSar: number | null; basicSalarySar: number | null };
  trips: TripRow[];
  totals: {
    tripCount: number; totalKm: number; derivedKm: number;
    signs: Record<LineKey, Sign>;
    baseSalary: Line;
    perKm: {
      rateSar: number; totalKm: number; derivedKm: number;
      kmOverride: number | null; amountSar: number;
      rateSource: 'sheet' | 'config' | 'default';
      basis: 'per_trip' | 'per_km';
      perTripRateSar: number | null;
      tripCount: number;
    };
    customLines: { label: string; amountSar: number; sign: Sign }[];
    customSar: number;
    dieselPetroapp: Line; dieselAldrees: Line;
    cashReceived: Line; cashReceivedHalaSar: number;
    maintenance: Line;
    totalAmountSar: number; deductionsSar: number;
    netSalarySar: number; receivedSar: number; pendingSar: number;
  };
  counts: { advances: number; maintenanceEvents: number };
  saved: { exists: boolean; periodEnd: string | null; notes: string | null };
  lock: {
    status: 'draft' | 'submitted';
    submittedAt: string | null;
    submittedBy: string | null;
    /** Whether THIS caller may still write. The server decides; we obey. */
    canEdit: boolean;
    pendingRequest: { id: string; note: string; requestedBy: string | null; requestedAt: string } | null;
  };
}

export const sar = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Text -> number for arithmetic. Blank and half-typed values count as zero. */
const val = (s: string) => {
  const n = Number(s);
  return s.trim() !== '' && Number.isFinite(n) ? n : 0;
};
/** Text -> what to send. Blank means "no value recorded". */
const toApi = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const isBadNumber = (s: string) => s.trim() !== '' && !Number.isFinite(Number(s));

const box = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-end font-mono ' +
  'focus:outline-none focus:ring-2 focus:ring-gold/40';

interface PerTripSite {
  siteId: string; siteName: string; siteCode: string;
  tripCount: number; rateSar: number; amountSar: number; rateSource: string;
}

interface PerTripLine {
  rateSar: number; tripCount: number; amountSar: number; rateSource: string;
}

const route = (t: TripRow) =>
  t.routeFrom || t.routeTo ? `${t.routeFrom ?? '?'} → ${t.routeTo ?? '?'}` : '—';

const ROW_GRID = 'grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] ' +
  'items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border';

// SignToggle and LineRow live at module scope on purpose.
//
// Declared inside the page component they were a NEW component type on every
// render, so React unmounted the row and mounted a fresh one after each
// keystroke - which threw focus away and meant one click per digit. The Per km
// row never had the bug because it is written inline. Anything holding an
// input has to keep a stable identity across renders.

function SignToggle({ value, onChange, label }: {
  value: Sign; onChange: (s: Sign) => void; label: string;
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden shrink-0" role="group" aria-label={`${label} direction`}>
      {(['plus', 'minus'] as const).map((s) => (
        <button
          key={s} type="button" onClick={() => onChange(s)} aria-pressed={value === s}
          title={s === 'plus' ? 'adds to the salary' : 'comes off the salary'}
          className={`w-8 py-1 text-sm leading-6 transition-colors ${
            value === s
              ? (s === 'plus' ? 'bg-emerald-500/20 text-emerald-700 font-semibold' : 'bg-rose-500/20 text-rose-700 font-semibold')
              : 'text-muted-foreground hover:bg-muted/40'
          }`}
        >
          {s === 'plus' ? '+' : '−'}
        </button>
      ))}
    </div>
  );
}

/** One salary line: name, note, one box, direction, effect. */
function LineRow({ label, note, sign, raw, effect, systemValue, onRaw, onSign, onReset }: {
  label: string; note: string; sign: Sign; raw: string; effect: number;
  systemValue?: number;
  onRaw: (v: string) => void; onSign: (s: Sign) => void; onReset?: () => void;
}) {
  const overridden = systemValue != null && raw.trim() !== '';
  return (
    <div className={ROW_GRID}>
      <div className="min-w-0">
        <div className="text-sm">{label}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {note}
          {overridden && onReset && (
            <>
              {' · '}
              <button type="button" className="underline hover:text-foreground" onClick={onReset}>
                use {sar(systemValue)}
              </button>
            </>
          )}
        </div>
      </div>
      <input
        className={`${box} col-start-1 sm:col-start-2 row-start-2 sm:row-start-1`}
        value={raw}
        onChange={(e) => onRaw(e.target.value)}
        inputMode="decimal"
        placeholder={systemValue != null ? sar(systemValue) : '0.00'}
        aria-label={label}
      />
      <div className="col-start-2 sm:col-start-3 row-start-2 sm:row-start-1 justify-self-end sm:justify-self-center">
        <SignToggle value={sign} onChange={onSign} label={label} />
      </div>
      <div className={`hidden sm:block text-end font-mono text-sm ${sign === 'minus' ? 'text-rose-600' : 'text-emerald-700'}`}>
        {sign === 'minus' ? '−' : '+'}{sar(effect)}
      </div>
    </div>
  );
}

/** All the text the sheet holds while it is being edited. */
interface Draft {
  base: string; petro: string; aldrees: string; cash: string; maint: string;
  hala: string; received: string;
  rate: string; km: string; tripRate: string;
}
const EMPTY: Draft = {
  base: '', petro: '', aldrees: '', cash: '', maint: '',
  hala: '', received: '', rate: '', km: '', tripRate: '',
};

export default function DriverSalarySheet({ driverId, from, to, side = 'dumpers' }: {
  driverId: string; from: string; to: string;
  /** Which side's trips this sheet is for. Defaults to dumpers, as it always was. */
  side?: 'dumpers' | 'neom';
}) {
  const qc = useQueryClient();

  const { data: sheet, isLoading, isError, error } = useQuery({
    queryKey: ['dumper-pay-sheet', side, driverId, from, to],
    queryFn: async () =>
      (await apiClient.get<Sheet>(`/api/v1/dumpers/driver-pay/sheet?side=${side}`, {
        params: { driverId, from, ...(to ? { to } : {}) },
      })).data,
  });

  const [d, setD] = useState<Draft>(EMPTY);
  /**
   * Rates typed against a site, held while editing and saved on blur.
   *
   * Kept apart from the sheet draft because a site rate belongs to the driver,
   * not to one month's sheet: set it once and every later period uses it.
   */
  const [siteRate, setSiteRate] = useState<Record<string, string>>({});

  const saveSiteRate = async (siteId: string) => {
    const raw = siteRate[siteId];
    if (raw === undefined || raw.trim() === '') return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return;
    try {
      await apiClient.post('/api/v1/dumpers/driver-pay/site-rate',
        { driverId, siteId, perTripRateSar: value },
        { params: { side } });
      await qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
    } catch (e) { setMsg(extractErrorMessage(e)); }
  };
  const [signs, setSigns] = useState<Record<LineKey, Sign> | null>(null);
  const [rows, setRows] = useState<CustomRow[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [showLoads, setShowLoads] = useState(false);

  const set = (k: keyof Draft, v: string) => { setD((p) => ({ ...p, [k]: v })); setMsg(null); };

  // Refill from the server whenever a different sheet arrives. A box left blank
  // means "use what the system says", so only real overrides fill one in.
  useEffect(() => {
    if (!sheet) return;
    const t = sheet.totals;
    const ov = (l: Line) => (l.override != null ? String(l.override) : '');
    const typed = (n: number) => (n ? String(n) : '');
    setD({
      base: ov(t.baseSalary),
      petro: ov(t.dieselPetroapp),
      aldrees: ov(t.dieselAldrees),
      cash: ov(t.cashReceived),
      maint: ov(t.maintenance),
      hala: typed(t.cashReceivedHalaSar),
      received: typed(t.receivedSar),
      rate: String(t.perKm.rateSar),
      tripRate: sheet.perTrip ? String(sheet.perTrip.rateSar) : '',
      km: t.perKm.kmOverride != null ? String(t.perKm.kmOverride) : '',
    });
    setSigns({ ...t.signs });
    setRows(t.customLines.map((l) => ({ label: l.label, amount: String(l.amountSar), sign: l.sign })));
    setMsg(null);
  }, [sheet]);

  // Everything recomputed from the text on screen, so a figure never lags a
  // keystroke behind what it is derived from.
  const live = useMemo(() => {
    if (!sheet) return null;
    const t = sheet.totals;
    const sg = signs ?? t.signs;
    const eff = (raw: string, l: Line) => (raw.trim() === '' ? l.derived : val(raw));

    const rate = d.rate.trim() === '' ? t.perKm.rateSar : val(d.rate);
    const km = d.km.trim() === '' ? t.perKm.derivedKm : val(d.km);
    // Typing a rate here outranks the per-trip rate on save, so the live figure
    // follows the same rule rather than a copy of it.
    const perTripNow = t.perKm.basis === 'per_trip' && d.rate.trim() === '';
    const tripPay = perTripNow ? (t.perKm.perTripRateSar ?? 0) * t.perKm.tripCount : rate * km;

    const values: Record<LineKey, number> = {
      baseSalary: eff(d.base, t.baseSalary),
      perKm: tripPay,
      dieselPetroapp: eff(d.petro, t.dieselPetroapp),
      dieselAldrees: eff(d.aldrees, t.dieselAldrees),
      cashReceived: eff(d.cash, t.cashReceived),
      cashReceivedHala: val(d.hala),
      maintenance: eff(d.maint, t.maintenance),
    };

    let plus = 0; let minus = 0;
    void perTripNow;
    (Object.keys(values) as LineKey[]).forEach((k) => {
      if (sg[k] === 'minus') minus += values[k]; else plus += values[k];
    });
    rows.forEach((r) => { if (r.sign === 'minus') minus += val(r.amount); else plus += val(r.amount); });

    const received = val(d.received);
    const net = plus - minus;
    return { sg, values, rate, km, plus, minus, net, received, pending: net - received };
  }, [sheet, d, signs, rows]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const numeric: (keyof Draft)[] = ['base', 'petro', 'aldrees', 'cash', 'maint', 'hala', 'received', 'rate', 'km'];
      if (numeric.some((k) => isBadNumber(d[k]))) throw new Error('One of the amounts is not a number.');
      if (rows.some((r) => !r.label.trim())) throw new Error('Every added row needs a name.');
      if (rows.some((r) => isBadNumber(r.amount))) throw new Error('Every added row needs a number.');

      return apiClient.put(`/api/v1/dumpers/driver-pay/sheet?side=${side}`, {
        driverId, from, to: to || null,
        baseSalarySar: toApi(d.base),
        cashReceivedHalaSar: val(d.hala),
        receivedSar: val(d.received),
        perKmRateSar: toApi(d.rate),
        perTripRateSar: toApi(d.tripRate),
        totalKmOverride: toApi(d.km),
        lineSigns: signs ?? undefined,
        customLines: rows.map((r) => ({ label: r.label.trim(), amountSar: val(r.amount), sign: r.sign })),
        dieselPetroappOverrideSar: toApi(d.petro),
        dieselAldreesOverrideSar: toApi(d.aldrees),
        cashReceivedOverrideSar: toApi(d.cash),
        maintenanceOverrideSar: toApi(d.maint),
      });
    },
    onSuccess: () => {
      setMsg('Saved.');
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
    },
    onError: (e) => setMsg(
      e instanceof Error && (e.message.includes('number') || e.message.includes('name'))
        ? e.message : extractErrorMessage(e),
    ),
  });

  // Save first, then lock: submitting a sheet whose typed values were never
  // sent would freeze the wrong numbers.
  const submitMut = useMutation({
    mutationFn: async () => {
      await saveMut.mutateAsync();
      return apiClient.post(`/api/v1/dumpers/driver-pay/sheet/submit?side=${side}`, { driverId, from });
    },
    onSuccess: () => {
      setMsg('Submitted. Only a super admin can change it now.');
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => setMsg(extractErrorMessage(e)),
  });

  const [askOpen, setAskOpen] = useState(false);
  const [askNote, setAskNote] = useState('');
  const requestMut = useMutation({
    mutationFn: async () => {
      if (!askNote.trim()) throw new Error('Say why it needs reopening.');
      return apiClient.post(`/api/v1/dumpers/driver-pay/sheet/request-edit?side=${side}`, {
        driverId, from, note: askNote.trim(),
      });
    },
    onSuccess: () => {
      setAskOpen(false); setAskNote('');
      setMsg('Request sent. A super admin will review it.');
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => setMsg(e instanceof Error && e.message.startsWith('Say why') ? e.message : extractErrorMessage(e)),
  });

  const setSign = (k: LineKey, s: Sign) => {
    setSigns((p) => (p ? { ...p, [k]: s } : p));
    setMsg(null);
  };
  const editRow = (i: number, patch: Partial<CustomRow>) => {
    setRows((rs) => rs.map((r, x) => (x === i ? { ...r, ...patch } : r)));
    setMsg(null);
  };

  function exportSheet() {
    if (!sheet || !live) return;
    const s = (k: LineKey) => (live.sg[k] === 'minus' ? -live.values[k] : live.values[k]);
    const aoa: (string | number)[][] = [
      ['Driver Salary'], [],
      ['Driver', sheet.driver.name, '', 'Period', sheet.period.from, 'to', sheet.period.to], [],
      ['Date', 'Driver', 'Vehicle', 'Route', 'Driver Salary', 'Trip Amount', 'Total Km',
       'Total Amount', 'Received', 'Pending', 'Vehicle Maintenance', 'Net Salary'],
    ];
    const salary: [string, number][] = [
      ['30 Days', s('baseSalary')],
      [t.perKm.basis === 'per_trip' && d.rate.trim() === ''
        ? `Per trip (${t.perKm.perTripRateSar} x ${t.perKm.tripCount} trips)`
        : `Per km (${live.rate} x ${live.km} km)`, s('perKm')],
      ['Diesel Received Petroapp', s('dieselPetroapp')],
      ['Diesel Received FleetPlus', s('dieselAldrees')],
      ['Cash Received', s('cashReceived')],
      ['Cash Received Hala', s('cashReceivedHala')],
      ...rows.map((r) => [r.label, r.sign === 'minus' ? -val(r.amount) : val(r.amount)] as [string, number]),
    ];
    for (let i = 0; i < Math.max(sheet.trips.length, salary.length); i++) {
      const tr = sheet.trips[i]; const l = salary[i];
      aoa.push([
        tr ? tr.date : '', tr ? sheet.driver.name : '', tr ? (tr.vehiclePlate ?? '') : '',
        tr ? route(tr) : '', l ? l[0] : '', l ? l[1] : (tr?.tripAmountSar ?? ''), tr?.km ?? '',
        '', '', '', '', '',
      ]);
    }
    aoa.push([]);
    aoa.push(['Total', `Trip Count ${sheet.totals.tripCount}`, '', '', '',
      live.values.perKm, live.km, live.plus, live.received, live.pending, s('maintenance'), live.net]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 28 }, { wch: 24 },
      { wch: 13 }, { wch: 10 }, { wch: 14 }, { wch: 11 }, { wch: 11 }, { wch: 20 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Driver Salary');
    XLSX.writeFile(wb, `driver-salary-${sheet.driver.code}-${sheet.period.from}.xlsx`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground px-4 py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (isError) {
    return (
      <p className="m-4 text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">
        {extractErrorMessage(error)}
      </p>
    );
  }
  if (!sheet || !live) return null;
  const t = sheet.totals;
  const locked = sheet.lock.status === 'submitted';
  const editable = sheet.lock.canEdit;

  return (
    <div className="p-4 space-y-4 bg-muted/10">
      {/* ---------- the trips, folded away: reference, not the point ---------- */}
      <section className="rounded-lg border border-border bg-card">
        <button
          type="button"
          onClick={() => setShowLoads((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-muted/20 transition-colors"
        >
          <span className="flex items-center gap-2">
            {showLoads ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <b>{t.tripCount}</b> trip{t.tripCount === 1 ? '' : 's'} · <b>{t.derivedKm}</b> km
            {sheet.trips[0] && <span className="text-muted-foreground hidden sm:inline">· {route(sheet.trips[0])}</span>}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); exportSheet(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); exportSheet(); } }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline"
          >
            <Download className="h-3.5 w-3.5" /> Export
          </span>
        </button>

        {showLoads && (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-start font-medium px-4 py-2">Date</th>
                  <th className="text-start font-medium px-4 py-2">Vehicle</th>
                  <th className="text-start font-medium px-4 py-2">Client</th>
                  <th className="text-end font-medium px-4 py-2">Km</th>
                  <th className="text-end font-medium px-4 py-2">Tons</th>
                </tr>
              </thead>
              <tbody>
                {sheet.trips.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    No trips for this driver in this period.
                  </td></tr>
                )}
                {sheet.trips.map((tr) => (
                  <tr key={tr.id} className="border-t border-border">
                    <td className="px-4 py-2 whitespace-nowrap">{tr.date}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{tr.vehiclePlate ?? '—'}</td>
                    <td className="px-4 py-2">{tr.clientName ?? '—'}</td>
                    <td className="px-4 py-2 text-end font-mono">{tr.km ?? '—'}</td>
                    <td className="px-4 py-2 text-end font-mono">{tr.tons ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ------------------------------ the salary ------------------------------ */}
      <section className="rounded-lg border border-border bg-card">
        <div className="px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            Driver salary
            {locked && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] normal-case tracking-normal">
                <Lock className="h-3 w-3" /> submitted
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editable && (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setD((p) => ({ ...p, base: '', petro: '', aldrees: '', cash: '', maint: '', km: '', rate: String(t.perKm.rateSar),
                  tripRate: sheet.perTrip ? String(sheet.perTrip.rateSar) : '' })); setMsg(null); }} className="gap-1">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to system
                </Button>
                <Button variant="outline" size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || submitMut.isPending} className="gap-1">
                  {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
              </>
            )}
            {editable && !locked && (
              <Button variant="gold" size="sm" onClick={() => submitMut.mutate()} disabled={submitMut.isPending || saveMut.isPending} className="gap-1">
                {submitMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Submit
              </Button>
            )}
            {!editable && !sheet.lock.pendingRequest && (
              <Button variant="outline" size="sm" onClick={() => setAskOpen((v) => !v)} className="gap-1">
                <MessageSquare className="h-4 w-4" /> Request edit
              </Button>
            )}
          </div>
        </div>

        {/* Why the boxes are dead, and what to do about it. */}
        {locked && (
          <div className={`px-4 py-2.5 border-t border-border text-xs ${editable ? 'bg-amber-500/5 text-amber-700' : 'bg-muted/30 text-muted-foreground'}`}>
            {editable ? (
              <>Submitted{sheet.lock.submittedBy ? ` by ${sheet.lock.submittedBy}` : ''}
                {sheet.lock.submittedAt ? ` on ${sheet.lock.submittedAt.slice(0, 10)}` : ''}.
                You are a super admin, so you can still change it — everyone else has to ask.</>
            ) : (
              <>Submitted{sheet.lock.submittedBy ? ` by ${sheet.lock.submittedBy}` : ''}
                {sheet.lock.submittedAt ? ` on ${sheet.lock.submittedAt.slice(0, 10)}` : ''} and closed for editing.
                {sheet.lock.pendingRequest
                  ? ` Your request is with a super admin: “${sheet.lock.pendingRequest.note}”`
                  : ' Request an edit to reopen it.'}</>
            )}
          </div>
        )}

        {askOpen && !editable && (
          <div className="px-4 py-3 border-t border-border bg-muted/20 space-y-2">
            <label className="text-xs text-muted-foreground" htmlFor="ask-note">
              Why does this sheet need reopening? A super admin reads this before deciding.
            </label>
            <textarea
              id="ask-note" rows={2} maxLength={2000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={askNote}
              onChange={(e) => { setAskNote(e.target.value); setMsg(null); }}
              placeholder="e.g. the diesel figure was wrong, Aldrees invoice arrived late"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setAskOpen(false); setAskNote(''); }}>Cancel</Button>
              <Button variant="gold" size="sm" onClick={() => requestMut.mutate()} disabled={requestMut.isPending} className="gap-1">
                {requestMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Send request
              </Button>
            </div>
          </div>
        )}

        {/* One disabled fieldset rather than a `disabled` prop on twenty
            inputs: the lock is one rule, so it should be enforced in one place.
            The browser then also blocks focus and submission for free. */}
        <fieldset disabled={!editable} className="m-0 p-0 border-0 min-w-0 disabled:opacity-60">

        {/*
            Per trip — NEOM only.

            The API sends this line for NEOM and omits it for dumpers, so the row
            appears exactly where the money is paid that way. Its rate is the
            driver's pay config rate: rates belong on the driver, not retyped
            into every sheet, and the count is the loads themselves.
        */}
        {sheet.perTrip && (sheet.perTripSites?.length ?? 0) > 0 && (
          <>
            {sheet.perTripSites!.map((r) => (
              <div key={r.siteId}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border">
                <div className="min-w-0">
                  <div className="text-sm">Per trip — {r.siteName}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.rateSource === 'site' ? 'rate set for this site'
                      : r.rateSource === 'unset' ? 'no rate set — set one in Set pay'
                      : `no site rate — using the driver's ${r.rateSource} rate`}
                    {' · '}{r.tripCount} trip{r.tripCount === 1 ? '' : 's'}
                  </div>
                </div>
                {/* One box, because only the rate is yours to set - the trip
                    count is what the loads say and typing over it would be a
                    number nobody could account for. */}
                <div className="col-start-1 sm:col-start-2 row-start-2 sm:row-start-1 flex items-center gap-2">
                  <input
                    className={box}
                    value={siteRate[r.siteId] ?? String(r.rateSar)}
                    inputMode="decimal"
                    aria-label={`Rate per trip at ${r.siteName}`}
                    onChange={(e) => setSiteRate((m) => ({ ...m, [r.siteId]: e.target.value }))}
                    onBlur={() => saveSiteRate(r.siteId)}
                  />
                  <span className="text-muted-foreground text-xs whitespace-nowrap">
                    × {r.tripCount} trip{r.tripCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="col-start-2 sm:col-start-3 row-start-2 sm:row-start-1 justify-self-end sm:justify-self-center" />
                <div className="hidden sm:block text-end font-mono text-sm text-emerald-700">
                  +{sar((Number(siteRate[r.siteId] ?? r.rateSar) || 0) * r.tripCount)}
                </div>
              </div>
            ))}
            {/* What the site rows come to, so the sheet still reconciles. */}
            <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 px-4 py-2 border-t border-border bg-muted/20">
              <div className="text-xs text-muted-foreground">
                Per trip, all sites — {sheet.perTripSites!.reduce((a, r) => a + r.tripCount, 0)} trips
                {' · '}{sheet.perTripSites!.map((r) => r.siteName).join(', ')}
              </div>
              <div /><div />
              <div className="hidden sm:block text-end font-mono text-sm">
                {sar(sheet.perTripSites!.reduce(
                  (a, r) => a + (Number(siteRate[r.siteId] ?? r.rateSar) || 0) * r.tripCount, 0))}
              </div>
            </div>
          </>
        )}

        {sheet.perTrip && (sheet.perTripSites?.length ?? 0) === 0 && (
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border">
            <div className="min-w-0">
              <div className="text-sm">Per trip</div>
              <div className="text-[11px] text-muted-foreground">
                {sheet.perTrip.rateSource === 'config'
                  ? 'rate from the pay config'
                  : sheet.perTrip.rateSource === 'sheet'
                    ? 'rate typed here'
                    : 'no per-trip rate set — set one in Set pay'}
                {' · '}{sheet.perTrip.tripCount} trip{sheet.perTrip.tripCount === 1 ? '' : 's'} from the loads
              </div>
            </div>
            <div className="col-start-1 sm:col-start-2 row-start-2 sm:row-start-1 flex items-center gap-1">
              <input className={box} value={d.tripRate}
                onChange={(e) => set('tripRate', e.target.value)}
                inputMode="decimal" placeholder={String(sheet.perTrip.rateSar)}
                aria-label="Rate per trip" />
              <span className="text-muted-foreground text-xs shrink-0">×</span>
              {/* The count is the loads themselves, so it is shown, not typed. */}
              <input className={box} value={sheet.perTrip.tripCount} readOnly
                aria-label="Number of trips" />
            </div>
            <div className="col-start-2 sm:col-start-3 row-start-2 sm:row-start-1 justify-self-end sm:justify-self-center">
              <SignToggle
                value={(live.sg as unknown as Record<string, Sign>).perTrip ?? 'plus'}
                onChange={(sg) => setSign('perTrip' as LineKey, sg)} label="Per trip" />
            </div>
            <div className="hidden sm:block text-end font-mono text-sm text-emerald-700">
              +{sar((Number(d.tripRate) || 0) * sheet.perTrip.tripCount)}
            </div>
          </div>
        )}

        {/* Per km gets two boxes, so it stands on its own. */}
        <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border">
          <div className="min-w-0">
            <div className="text-sm">
              {t.perKm.basis === 'per_trip' && d.rate.trim() === '' ? 'Per trip' : 'Per km'}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t.perKm.basis === 'per_trip' && d.rate.trim() === ''
                ? `${t.perKm.perTripRateSar} per trip × ${t.perKm.tripCount} trip${t.perKm.tripCount === 1 ? '' : 's'} · type a rate to pay by km instead`
                : (
                  <>
                    {t.perKm.rateSource === 'sheet' ? 'rate typed here'
                      : t.perKm.rateSource === 'config' ? 'rate from the pay config'
                      : 'default rate 0.85'}
                    {' · '}{d.km.trim() === '' ? `${t.perKm.derivedKm} km from the trips` : `km typed, trips say ${t.perKm.derivedKm}`}
                  </>
                )}
            </div>
          </div>
          <div className="col-start-1 sm:col-start-2 row-start-2 sm:row-start-1 flex items-center gap-1">
            <input
              className={box} value={d.rate} onChange={(e) => set('rate', e.target.value)}
              inputMode="decimal" placeholder="0.85" aria-label="Rate per km"
            />
            <span className="text-muted-foreground text-xs shrink-0">×</span>
            <input
              className={box} value={d.km} onChange={(e) => set('km', e.target.value)}
              inputMode="decimal" placeholder={String(t.perKm.derivedKm)} aria-label="Total km"
            />
          </div>
          <div className="col-start-2 sm:col-start-3 row-start-2 sm:row-start-1 justify-self-end sm:justify-self-center">
            <SignToggle value={live.sg.perKm} onChange={(s) => setSign('perKm', s)} label="Per km" />
          </div>
          <div className={`hidden sm:block text-end font-mono text-sm ${live.sg.perKm === 'minus' ? 'text-rose-600' : 'text-emerald-700'}`}>
            {live.sg.perKm === 'minus' ? '−' : '+'}{sar(live.values.perKm)}
          </div>
        </div>

        <LineRow
          label="30 Days"
          note={sheet.rates.basicSalarySar == null ? 'no basic salary on file — type one here' : 'from the pay config'}
          sign={live.sg.baseSalary} raw={d.base} effect={live.values.baseSalary}
          systemValue={t.baseSalary.derived}
          onRaw={(v) => set('base', v)} onSign={(s) => setSign('baseSalary', s)} onReset={() => set('base', '')}
        />
        <LineRow
          label="Diesel Received Petroapp" note="from the fuel module"
          sign={live.sg.dieselPetroapp} raw={d.petro} effect={live.values.dieselPetroapp}
          systemValue={t.dieselPetroapp.derived}
          onRaw={(v) => set('petro', v)} onSign={(s) => setSign('dieselPetroapp', s)} onReset={() => set('petro', '')}
        />
        <LineRow
          label="Diesel Received FleetPlus" note="from the fuel module"
          sign={live.sg.dieselAldrees} raw={d.aldrees} effect={live.values.dieselAldrees}
          systemValue={t.dieselAldrees.derived}
          onRaw={(v) => set('aldrees', v)} onSign={(s) => setSign('dieselAldrees', s)} onReset={() => set('aldrees', '')}
        />
        <LineRow
          label="Cash Received" note={`${sheet.counts.advances} advance(s) this period`}
          sign={live.sg.cashReceived} raw={d.cash} effect={live.values.cashReceived}
          systemValue={t.cashReceived.derived}
          onRaw={(v) => set('cash', v)} onSign={(s) => setSign('cashReceived', s)} onReset={() => set('cash', '')}
        />
        <LineRow
          label="Cash Received Hala" note="typed — no system source"
          sign={live.sg.cashReceivedHala} raw={d.hala} effect={live.values.cashReceivedHala}
          onRaw={(v) => set('hala', v)} onSign={(s) => setSign('cashReceivedHala', s)}
        />
        <LineRow
          label="Vehicle Maintenance" note={`${sheet.counts.maintenanceEvents} event(s) charged here`}
          sign={live.sg.maintenance} raw={d.maint} effect={live.values.maintenance}
          systemValue={t.maintenance.derived}
          onRaw={(v) => set('maint', v)} onSign={(s) => setSign('maintenance', s)} onReset={() => set('maint', '')}
        />

        {/* rows the operator writes themselves */}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border bg-muted/10">
            <input
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
              value={r.label}
              maxLength={120}
              onChange={(e) => editRow(i, { label: e.target.value })}
              placeholder="name this row…"
              aria-label={`Row ${i + 1} name`}
            />
            <input
              className={`${box} col-start-1 sm:col-start-2 row-start-2 sm:row-start-1`}
              value={r.amount}
              onChange={(e) => editRow(i, { amount: e.target.value })}
              inputMode="decimal" placeholder="0.00" aria-label={`Row ${i + 1} amount`}
            />
            <div className="col-start-2 sm:col-start-3 row-start-2 sm:row-start-1 justify-self-end sm:justify-self-center flex items-center gap-1">
              <SignToggle value={r.sign} onChange={(s) => editRow(i, { sign: s })} label={r.label || `Row ${i + 1}`} />
              <button
                type="button"
                onClick={() => { setRows((rs) => rs.filter((_, x) => x !== i)); setMsg(null); }}
                className="text-muted-foreground hover:text-destructive p-1"
                aria-label={`Remove row ${i + 1}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className={`hidden sm:block text-end font-mono text-sm ${r.sign === 'minus' ? 'text-rose-600' : 'text-emerald-700'}`}>
              {r.sign === 'minus' ? '−' : '+'}{sar(val(r.amount))}
            </div>
          </div>
        ))}

        <div className="px-4 py-2.5 border-t border-border">
          <Button type="button" variant="outline" size="sm"
            onClick={() => { setRows((rs) => [...rs, { label: '', amount: '', sign: 'plus' }]); setMsg(null); }}
            className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Add a row
          </Button>
        </div>

        {/* Received is a payment against the salary, not a line of it. */}
        <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_9rem_auto_7rem] items-center gap-x-3 gap-y-1 px-4 py-2.5 border-t border-border bg-muted/20">
          <div>
            <div className="text-sm">Received</div>
            <div className="text-[11px] text-muted-foreground">already paid — only affects Pending</div>
          </div>
          <input
            className={`${box} col-start-1 sm:col-start-2 row-start-2 sm:row-start-1`}
            value={d.received} onChange={(e) => set('received', e.target.value)}
            inputMode="decimal" placeholder="0.00" aria-label="Received"
          />
          <div />
          <div className="hidden sm:block text-end font-mono text-sm">{sar(live.received)}</div>
        </div>

        </fieldset>

        {msg && (
          <p className={`px-4 py-2 text-sm border-t border-border ${msg.startsWith('Saved') ? 'text-emerald-600' : 'text-destructive'}`}>
            {msg}
          </p>
        )}
      </section>

      {/* ------------------------------- the result ------------------------------ */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Adds up to', value: live.plus, hint: 'every + row', tone: '' },
          { label: 'Comes off', value: live.minus, hint: 'every − row', tone: '' },
          { label: 'Pending', value: live.pending, hint: 'net − received', tone: live.pending > 0 ? 'amber' : '' },
          { label: 'Net Salary', value: live.net, hint: 'the + rows minus the − rows', tone: 'gold' },
        ].map((c) => (
          <div key={c.label} className={`rounded-lg border p-3 ${
            c.tone === 'gold' ? 'border-gold/50 bg-gold/5'
            : c.tone === 'amber' ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'
          }`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
            <div className="text-xl font-display mt-1 font-mono">{sar(c.value)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{c.hint}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
